/**
 * /admin/users — Admin-only user management page (Phase B.5).
 *
 * Lists all users + roles. Admins can:
 *  - Invite a new user (email + role) — adds them to allowlist; they
 *    can then sign in with Google.
 *  - Change someone's role on the fly.
 *  - Suspend / re-activate an account.
 *  - Delete an account permanently.
 */
import React, { useEffect, useState } from "react";
import {
  UserPlus,
  ShieldCheck,
  ShieldAlert,
  Loader2,
  Trash2,
  Crown,
  Briefcase,
  Users,
  Bus,
  CircleUser,
  History,
  Activity,
  Truck,
  CheckSquare,
  Send,
  Calendar,
  Phone,
  RotateCcw,
  Package,
  MapPin,
  FileText,
  AlertTriangle,
  UserMinus,
  Pencil,
} from "lucide-react";
import {
  fetchUsers,
  inviteUser,
  patchUser,
  removeUser,
  authConfig,
  fetchAuditLog,
  bulkAssignTasks,
  unassignAllDispensaries,
} from "../lib/api";
import { useAuth } from "../lib/AuthProvider";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const ROLE_OPTIONS = [
  { value: "admin",      label: "Admin",      Icon: Crown,        desc: "Full access, manages roles" },
  { value: "dispatcher", label: "Dispatcher", Icon: Briefcase,    desc: "Schedules bus events, builds routes" },
  { value: "sales",      label: "Sales",      Icon: Users,        desc: "Owns assigned accounts only" },
  { value: "lead_host",  label: "Lead Host",  Icon: ShieldCheck,  desc: "Read-only, in charge at their bus events" },
  { value: "host",       label: "Host",       Icon: Bus,          desc: "Read-only, attends bus events" },
  { value: "driver",     label: "Driver",     Icon: Truck,        desc: "Read-only, drives the bus to scheduled events" },
];

const ROLE_META = Object.fromEntries(ROLE_OPTIONS.map((r) => [r.value, r]));

const ROLE_COLOR = {
  admin:      "bg-rose-500/15    text-rose-200    border-rose-500/30",
  dispatcher: "bg-amber-500/15   text-amber-200   border-amber-500/30",
  sales:      "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
  lead_host:  "bg-violet-500/15  text-violet-200  border-violet-500/30",
  host:       "bg-cyan-500/15    text-cyan-200    border-cyan-500/30",
  driver:     "bg-orange-500/15  text-orange-200  border-orange-500/30",
};

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState(null);
  const [allowedDomains, setAllowedDomains] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("sales");
  const [newName, setNewName] = useState("");

  const load = async () => {
    const list = await fetchUsers();
    setUsers(list);
  };

  useEffect(() => {
    load();
    authConfig().then((d) => setAllowedDomains(d.allowed_domains || []));
  }, []);

  const handleInvite = async (e) => {
    e?.preventDefault?.();
    const email = newEmail.trim().toLowerCase();
    if (!email) {
      toast.error("Email is required");
      return;
    }
    setInviting(true);
    try {
      await inviteUser({ email, role: newRole, name: newName.trim() });
      toast.success(`Added ${email}`, { description: `${ROLE_META[newRole].label} · they can sign in with Google now` });
      setNewEmail("");
      setNewName("");
      setNewRole("sales");
      load();
    } catch (err) {
      toast.error("Could not invite user", {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setInviting(false);
    }
  };

  const changeRole = async (u, role) => {
    try {
      await patchUser(u.user_id, { role });
      toast.success(`Role updated · ${u.email}`);
      load();
    } catch (err) {
      toast.error("Could not change role", {
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  const toggleActive = async (u) => {
    try {
      await patchUser(u.user_id, { active: !u.active });
      toast.success(u.active ? "Account suspended" : "Account reactivated");
      load();
    } catch (err) {
      toast.error("Could not update", {
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  const remove = async (u) => {
    if (
      !window.confirm(`Permanently delete user ${u.email}? This cannot be undone.`)
    )
      return;
    try {
      await removeUser(u.user_id);
      toast.success(`Removed ${u.email}`);
      load();
    } catch (err) {
      toast.error("Could not delete", {
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  const [editing, setEditing] = useState(null); // user object | null
  const saveEdit = async (changes) => {
    try {
      await patchUser(editing.user_id, changes);
      toast.success(`Updated ${changes.email || editing.email}`);
      setEditing(null);
      load();
    } catch (err) {
      toast.error("Could not save", {
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 fade-up">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Users & Roles</h2>
        <p className="text-sm text-zinc-500 mt-1">
          Manage who can sign into JackTrack. Only emails on{" "}
          {allowedDomains.map((d, i) => (
            <span key={d} className="text-zinc-300 font-mono">
              @{d}
              {i < allowedDomains.length - 1 ? " or " : ""}
            </span>
          ))}{" "}
          are accepted.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-zinc-800">
        <button
          type="button"
          data-testid="tab-users"
          onClick={() => setTab("users")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === "users"
              ? "border-emerald-400 text-emerald-300"
              : "border-transparent text-zinc-400 hover:text-zinc-200"
          }`}
        >
          <span className="inline-flex items-center gap-2">
            <Users className="w-4 h-4" />
            Users
          </span>
        </button>
        <button
          type="button"
          data-testid="tab-bulk-tasks"
          onClick={() => setTab("bulk_tasks")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === "bulk_tasks"
              ? "border-emerald-400 text-emerald-300"
              : "border-transparent text-zinc-400 hover:text-zinc-200"
          }`}
        >
          <span className="inline-flex items-center gap-2">
            <CheckSquare className="w-4 h-4" />
            Bulk Tasks
          </span>
        </button>
        <button
          type="button"
          data-testid="tab-audit-log"
          onClick={() => setTab("audit")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === "audit"
              ? "border-emerald-400 text-emerald-300"
              : "border-transparent text-zinc-400 hover:text-zinc-200"
          }`}
        >
          <span className="inline-flex items-center gap-2">
            <History className="w-4 h-4" />
            Activity Log
          </span>
        </button>
      </div>

      {tab === "audit" ? (
        <AuditLogTab />
      ) : tab === "bulk_tasks" ? (
        <BulkTasksTab users={users || []} />
      ) : (
        <UsersTab
          me={me}
          users={users}
          inviting={inviting}
          newEmail={newEmail}
          setNewEmail={setNewEmail}
          newRole={newRole}
          setNewRole={setNewRole}
          newName={newName}
          setNewName={setNewName}
          handleInvite={handleInvite}
          changeRole={changeRole}
          toggleActive={toggleActive}
          remove={remove}
          startEdit={setEditing}
        />
      )}
      {editing && (
        <EditUserDialog
          user={editing}
          allowedDomains={allowedDomains}
          onClose={() => setEditing(null)}
          onSave={saveEdit}
        />
      )}
    </div>
  );
}

function UsersTab({ me, users, inviting, newEmail, setNewEmail, newRole, setNewRole, newName, setNewName, handleInvite, changeRole, toggleActive, remove, startEdit }) {
  return (
    <div className="space-y-6">
      {/* Invite form */}
      <div className="panel p-5 border border-emerald-500/20">
        <div className="flex items-center gap-2 mb-3">
          <UserPlus className="w-4 h-4 text-emerald-400" />
          <h3 className="text-sm font-semibold">Add new user</h3>
        </div>
        <form
          onSubmit={handleInvite}
          className="grid grid-cols-1 sm:grid-cols-[2fr_1.5fr_1.5fr_auto] gap-2"
        >
          <Input
            data-testid="invite-email-input"
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="name@calyfx.com"
            className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9"
          />
          <Input
            data-testid="invite-name-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Display name (optional)"
            className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9"
          />
          <Select value={newRole} onValueChange={setNewRole}>
            <SelectTrigger
              data-testid="invite-role-select"
              className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
              {ROLE_OPTIONS.map((o) => {
                const Icon = o.Icon;
                return (
                  <SelectItem key={o.value} value={o.value}>
                    <span className="inline-flex items-center gap-2">
                      <Icon className="w-3.5 h-3.5" />
                      {o.label}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <Button
            type="submit"
            data-testid="invite-submit-btn"
            disabled={inviting || !newEmail.trim()}
            className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 h-9"
          >
            {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add user"}
          </Button>
        </form>
        <p className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
          {ROLE_META[newRole].desc}.{" "}
          <span className="text-zinc-600">
            No invite email is sent — the user can sign in with Google right
            away using the email you entered.
          </span>
        </p>
      </div>

      {/* Users list */}
      <div className="panel p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 border-b border-zinc-800">
            <tr className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
              <th className="text-left px-4 py-2.5">User</th>
              <th className="text-left px-4 py-2.5">Role</th>
              <th className="text-left px-4 py-2.5">Last login</th>
              <th className="text-right px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {users === null ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-zinc-500">
                  Loading…
                </td>
              </tr>
            ) : (
              users.map((u) => {
                const meta = ROLE_META[u.role] || ROLE_META.sales;
                const Icon = meta.Icon;
                const isMe = me?.user_id === u.user_id;
                return (
                  <tr
                    key={u.user_id}
                    data-testid={`user-row-${u.user_id}`}
                    className={`border-b border-zinc-800/60 ${
                      !u.active ? "opacity-50" : ""
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {u.picture ? (
                          <img
                            src={u.picture}
                            alt=""
                            className="w-7 h-7 rounded-full"
                          />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center">
                            <CircleUser className="w-4 h-4 text-zinc-500" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="text-zinc-100 font-medium">
                            {u.name || u.email.split("@")[0]}
                            {isMe && (
                              <span className="ml-2 text-[10px] uppercase font-mono text-emerald-300">
                                you
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-zinc-500 font-mono">
                            {u.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Select
                        value={u.role}
                        onValueChange={(v) => changeRole(u, v)}
                        disabled={isMe}
                      >
                        <SelectTrigger
                          data-testid={`role-select-${u.user_id}`}
                          className={`h-7 px-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider border w-auto ${
                            ROLE_COLOR[u.role] || ROLE_COLOR.sales
                          }`}
                        >
                          <Icon className="w-3 h-3" />
                          <SelectValue>{meta.label}</SelectValue>
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                          {ROLE_OPTIONS.map((o) => {
                            const RIcon = o.Icon;
                            return (
                              <SelectItem key={o.value} value={o.value}>
                                <span className="inline-flex items-center gap-2">
                                  <RIcon className="w-3.5 h-3.5" />
                                  {o.label}
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-500 font-mono">
                      {u.last_login_at
                        ? new Date(u.last_login_at).toLocaleString()
                        : "Never"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => toggleActive(u)}
                        disabled={isMe}
                        data-testid={`toggle-active-${u.user_id}`}
                        className={`text-[10px] uppercase tracking-wider font-mono px-2 py-1 rounded border ${
                          u.active
                            ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20"
                            : "border-rose-500/30 text-rose-300 bg-rose-500/10 hover:bg-rose-500/20"
                        } ${isMe ? "opacity-30 cursor-not-allowed" : ""}`}
                      >
                        {u.active ? "Active" : "Suspended"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-0.5">
                        <button
                          data-testid={`edit-user-${u.user_id}`}
                          onClick={() => startEdit(u)}
                          className="text-zinc-500 hover:text-emerald-300 p-1.5 rounded"
                          aria-label="Edit user"
                          title="Edit user"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        {!isMe && (
                          <button
                            data-testid={`delete-user-${u.user_id}`}
                            onClick={() => remove(u)}
                            className="text-zinc-500 hover:text-rose-300 p-1.5 rounded"
                            aria-label="Delete user"
                            title="Delete user"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <DangerZone />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Danger Zone — mass-unassign all dispensaries. Used during launch / reset
// flows so the team can manually re-assign accounts from a clean slate.
// ---------------------------------------------------------------------------
function DangerZone() {
  const [confirm, setConfirm] = useState("");
  const [working, setWorking] = useState(false);

  const run = async () => {
    if (confirm !== "UNASSIGN") {
      toast.error("Type UNASSIGN to confirm");
      return;
    }
    setWorking(true);
    try {
      const res = await unassignAllDispensaries();
      toast.success(`Cleared assignment on ${res.cleared} dispensar${res.cleared === 1 ? "y" : "ies"}`, {
        description: "Every account is now unassigned. Re-assign from the Dispensaries page.",
      });
      setConfirm("");
    } catch (err) {
      toast.error("Could not unassign", {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setWorking(false);
    }
  };

  return (
    <div
      data-testid="danger-zone"
      className="mt-6 panel p-5 border border-rose-500/30 bg-rose-500/5 space-y-3"
    >
      <div className="flex items-start gap-2.5">
        <AlertTriangle className="w-4 h-4 text-rose-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-rose-200 tracking-tight">
            Danger zone
          </h3>
          <p className="text-xs text-rose-200/70 mt-1 leading-relaxed">
            <strong className="text-rose-100">Unassign all dispensaries</strong> —
            clears the sales-rep assignment on every account so the team can
            manually re-assign from a clean slate. Useful at launch or after a
            territory reshuffle. <em>Dispensary records, users, tasks,
            interactions, and notes are not touched.</em> The action is audited.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Input
          data-testid="danger-zone-confirm-input"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder='Type UNASSIGN to confirm'
          className="flex-1 min-w-[200px] bg-zinc-950 border-rose-500/30 text-zinc-100 placeholder:text-zinc-600 font-mono"
        />
        <Button
          data-testid="danger-zone-unassign-btn"
          onClick={run}
          disabled={confirm !== "UNASSIGN" || working}
          className="bg-rose-600 hover:bg-rose-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-rose-50 rounded-md"
        >
          {working ? (
            <>
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              Unassigning…
            </>
          ) : (
            <>
              <UserMinus className="w-4 h-4 mr-1.5" />
              Unassign all dispensaries
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit user dialog — admin can correct name + email + role on an existing
// user. Email changes also re-sync any Contact row pointing at the old
// email and kill the user's sessions so the next sign-in picks up the
// new identity.
// ---------------------------------------------------------------------------
function EditUserDialog({ user, allowedDomains, onClose, onSave }) {
  const [name, setName] = useState(user.name || "");
  const [email, setEmail] = useState(user.email || "");
  const [role, setRole] = useState(user.role);
  const [saving, setSaving] = useState(false);

  const dirty =
    (name || "") !== (user.name || "") ||
    email.trim().toLowerCase() !== (user.email || "").toLowerCase() ||
    role !== user.role;

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!dirty) {
      onClose();
      return;
    }
    setSaving(true);
    const changes = {};
    if ((name || "") !== (user.name || "")) changes.name = name.trim();
    if (email.trim().toLowerCase() !== (user.email || "").toLowerCase())
      changes.email = email.trim().toLowerCase();
    if (role !== user.role) changes.role = role;
    try {
      await onSave(changes);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="edit-user-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="text-zinc-50">Edit user</DialogTitle>
          <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
            Correct a misspelled name, fix an incorrect email, or change a
            role. Email must be on{" "}
            {allowedDomains.map((d, i) => (
              <span key={d} className="text-zinc-300 font-mono">
                @{d}
                {i < allowedDomains.length - 1 ? " or " : ""}
              </span>
            ))}
            . Changing an email signs the user out of any active sessions.
          </p>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-3 mt-2">
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400">
              Display name
            </Label>
            <Input
              data-testid="edit-user-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Display name"
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400">
              Email
            </Label>
            <Input
              data-testid="edit-user-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@calyfx.com"
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider font-mono text-zinc-400">
              Role
            </Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger
                data-testid="edit-user-role"
                className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                {ROLE_OPTIONS.map((o) => {
                  const Icon = o.Icon;
                  return (
                    <SelectItem key={o.value} value={o.value}>
                      <span className="inline-flex items-center gap-2">
                        <Icon className="w-3.5 h-3.5" />
                        {o.label}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-zinc-600 mt-1 font-mono">
              {ROLE_META[role]?.desc}
            </p>
          </div>
          <DialogFooter className="mt-4 gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-100"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="edit-user-save"
              disabled={saving || !dirty || !email.trim()}
              className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-emerald-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Activity Log — friendly plain-English "Who · When · What" feed.
// ---------------------------------------------------------------------------
const ACTION_VERB = {
  create: { verb: "created", color: "text-emerald-300" },
  update: { verb: "updated", color: "text-sky-300" },
  delete: { verb: "deleted", color: "text-rose-300" },
  login:  { verb: "signed in", color: "text-zinc-300" },
};

const ENTITY_LABEL = {
  dispensary: "a dispensary",
  user: "a user",
  bus_tasting: "a bus event",
  interaction: "an interaction",
  task: "a task",
  contact: "a contact",
  dispensary_contact: "a dispensary contact",
  scan: "a scan",
};

function relativeTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso);
  const now = new Date();
  const diff = Math.round((now - then) / 1000);
  if (diff < 60)        return "just now";
  if (diff < 3600)      return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)     return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return then.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function exactTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function AuditLogTab() {
  const [log, setLog] = useState(null);
  const [userFilter, setUserFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");

  useEffect(() => {
    fetchAuditLog(500).then(setLog).catch(() => setLog([]));
  }, []);

  // Build user dropdown options from the log itself
  const userOptions = React.useMemo(() => {
    const seen = new Map();
    (log || []).forEach((row) => {
      if (row.user_email && !seen.has(row.user_email)) {
        seen.set(row.user_email, row.user_email.split("@")[0]);
      }
    });
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [log]);

  const filtered = React.useMemo(() => {
    if (!log) return null;
    return log.filter(
      (r) =>
        (userFilter === "all" || r.user_email === userFilter) &&
        (actionFilter === "all" || r.action === actionFilter)
    );
  }, [log, userFilter, actionFilter]);

  if (log === null) {
    return (
      <div className="text-sm text-zinc-500 flex items-center gap-2 px-1 py-6">
        <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />
        Loading activity…
      </div>
    );
  }
  if (log.length === 0) {
    return (
      <div className="panel p-6 text-center text-sm text-zinc-500">
        No activity recorded yet. Anything anyone changes — sales notes,
        bus events, account assignments — will show up here.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-xs text-zinc-500 leading-relaxed">
        Every change to dispensaries, bus events, tasks, contacts, and users is
        recorded here for the last year. Most recent at the top.
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
          Show:
        </span>
        <Select value={userFilter} onValueChange={setUserFilter}>
          <SelectTrigger
            data-testid="audit-filter-user"
            className="bg-zinc-900 border-zinc-800 text-zinc-100 h-8 w-auto min-w-[160px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
            <SelectItem value="all">Everyone</SelectItem>
            {userOptions.map(([email, name]) => (
              <SelectItem key={email} value={email}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger
            data-testid="audit-filter-action"
            className="bg-zinc-900 border-zinc-800 text-zinc-100 h-8 w-auto min-w-[160px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
            <SelectItem value="all">All actions</SelectItem>
            <SelectItem value="create">Created</SelectItem>
            <SelectItem value="update">Updated</SelectItem>
            <SelectItem value="delete">Deleted</SelectItem>
            <SelectItem value="login">Signed in</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-[10px] font-mono text-zinc-500 ml-auto">
          Showing {filtered?.length || 0} of {log.length}
        </span>
      </div>

      <ol className="space-y-2" data-testid="audit-log-list">
        {(filtered || []).map((row) => {
          const action = ACTION_VERB[row.action] || {
            verb: row.action,
            color: "text-zinc-300",
          };
          const what = ENTITY_LABEL[row.entity_type] || row.entity_type;
          const who =
            (row.user_email || "").split("@")[0] ||
            row.user_email ||
            "Someone";
          return (
            <li
              key={row.id}
              data-testid={`audit-row-${row.id}`}
              className="panel px-4 py-3 flex items-start gap-3"
            >
              <div className="shrink-0 mt-1 w-7 h-7 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center">
                <Activity className="w-3.5 h-3.5 text-zinc-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-200 leading-snug">
                  <span className="font-semibold text-zinc-100">{who}</span>{" "}
                  <span className={action.color}>{action.verb}</span>{" "}
                  <span className="text-zinc-400">{what}</span>
                </div>
                {row.summary && (
                  <div className="text-xs text-zinc-500 mt-0.5 truncate">
                    {row.summary}
                  </div>
                )}
              </div>
              <div
                className="shrink-0 text-[11px] text-zinc-500 font-mono pt-1"
                title={exactTime(row.created_at)}
              >
                {relativeTime(row.created_at)}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Bulk Tasks tab — admin fans out one task template across every dispensary
// owned by selected reps. Powers the "Call back this week's lapsed accounts"
// flow without admin having to click through 30 dispensary detail pages.
// ---------------------------------------------------------------------------
const TASK_TYPE_OPTIONS = [
  { value: "call_back",      label: "Call back",      Icon: Phone },
  { value: "follow_up",      label: "Follow up",      Icon: RotateCcw },
  { value: "send_sample",    label: "Send sample",    Icon: Package },
  { value: "send_proposal",  label: "Send proposal",  Icon: Send },
  { value: "schedule_visit", label: "Schedule visit", Icon: MapPin },
  { value: "other",          label: "Other",          Icon: FileText },
];

function BulkTasksTab({ users }) {
  const assignableUsers = (users || []).filter(
    (u) => u.active && ["sales", "admin"].includes(u.role)
  );
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [taskType, setTaskType] = useState("follow_up");
  const [dueDate, setDueDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const toggleUser = (uid) => {
    setSelectedUserIds((cur) =>
      cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid]
    );
  };

  const allSelected =
    assignableUsers.length > 0 &&
    selectedUserIds.length === assignableUsers.length;
  const toggleAll = () => {
    setSelectedUserIds(
      allSelected ? [] : assignableUsers.map((u) => u.user_id)
    );
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!title.trim()) {
      toast.error("Task title is required");
      return;
    }
    if (selectedUserIds.length === 0) {
      toast.error("Pick at least one rep");
      return;
    }
    try {
      setSubmitting(true);
      const res = await bulkAssignTasks({
        user_ids: selectedUserIds,
        title: title.trim(),
        description: description.trim(),
        task_type: taskType,
        due_date: dueDate || null,
      });
      setLastResult(res);
      toast.success(
        `${res.total_created} task${res.total_created === 1 ? "" : "s"} created`
      );
      // Don't clear form — admin may want to repeat the assignment.
    } catch (err) {
      toast.error("Bulk assign failed", {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="bulk-tasks-tab">
      <div className="panel p-5 border border-emerald-500/20">
        <div className="flex items-center gap-2 mb-3">
          <CheckSquare className="w-4 h-4 text-emerald-400" />
          <h3 className="text-sm font-semibold tracking-tight">
            Bulk-assign a task
          </h3>
        </div>
        <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
          Spawns one task on every dispensary owned by the selected reps.
          Useful for cadenced outreach like
          {" "}<span className="text-zinc-300">"Call back this week's lapsed accounts"</span>{" "}
          or quarterly sample drops. Each rep sees their new tasks instantly
          under "My Open Tasks".
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
              Task title
            </label>
            <Input
              data-testid="bulk-task-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Call back about new SKU launch"
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
                Task type
              </label>
              <Select value={taskType} onValueChange={setTaskType}>
                <SelectTrigger
                  data-testid="bulk-task-type"
                  className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                  {TASK_TYPE_OPTIONS.map((o) => {
                    const Icon = o.Icon;
                    return (
                      <SelectItem key={o.value} value={o.value}>
                        <span className="inline-flex items-center gap-2">
                          <Icon className="w-3.5 h-3.5" />
                          {o.label}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
                Due date (optional)
              </label>
              <Input
                data-testid="bulk-task-due"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="bg-zinc-900 border-zinc-800 text-zinc-100 font-mono mt-1"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
              Notes / context (optional)
            </label>
            <Input
              data-testid="bulk-task-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What context should the rep have?"
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
                Assign to reps
              </label>
              <button
                type="button"
                data-testid="bulk-task-toggle-all"
                onClick={toggleAll}
                className="text-[11px] text-emerald-400 hover:text-emerald-300 font-mono"
              >
                {allSelected ? "Clear all" : "Select all"}
              </button>
            </div>
            {assignableUsers.length === 0 ? (
              <div className="text-xs text-zinc-500 italic py-3">
                No active sales reps to assign. Invite a rep from the Users tab first.
              </div>
            ) : (
              <ul className="space-y-1.5">
                {assignableUsers.map((u) => {
                  const checked = selectedUserIds.includes(u.user_id);
                  return (
                    <li key={u.user_id}>
                      <button
                        type="button"
                        data-testid={`bulk-task-user-${u.user_id}`}
                        onClick={() => toggleUser(u.user_id)}
                        className={`w-full flex items-center gap-3 rounded-md border px-3 py-2 transition-colors text-left ${
                          checked
                            ? "border-emerald-500/40 bg-emerald-500/5"
                            : "border-zinc-800 bg-zinc-950/40 hover:border-zinc-700"
                        }`}
                      >
                        <span
                          className={`shrink-0 w-4 h-4 rounded border flex items-center justify-center ${
                            checked
                              ? "bg-emerald-500 border-emerald-500 text-emerald-50"
                              : "border-zinc-700"
                          }`}
                        >
                          {checked && <CheckSquare className="w-3 h-3" />}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-zinc-100 truncate">
                            {u.name || u.email}
                          </div>
                          <div className="text-[11px] text-zinc-500 font-mono truncate">
                            {u.email}
                          </div>
                        </div>
                        <span
                          className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border ${
                            ROLE_COLOR[u.role] || "bg-zinc-500/15 text-zinc-300 border-zinc-500/30"
                          }`}
                        >
                          {u.role}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button
              type="submit"
              data-testid="bulk-task-submit"
              disabled={submitting || !title.trim() || selectedUserIds.length === 0}
              className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Assign to {selectedUserIds.length || 0} rep
              {selectedUserIds.length === 1 ? "" : "s"}
            </Button>
          </div>
        </form>
      </div>

      {lastResult && (
        <div className="panel p-5" data-testid="bulk-task-result">
          <h4 className="text-xs uppercase tracking-wider font-mono text-zinc-500 mb-3">
            Last assignment
          </h4>
          <div className="text-sm text-zinc-100 mb-2">
            {lastResult.total_created} task
            {lastResult.total_created === 1 ? "" : "s"} created across{" "}
            {lastResult.results.length} rep
            {lastResult.results.length === 1 ? "" : "s"}.
          </div>
          <ul className="space-y-1">
            {lastResult.results.map((r) => (
              <li
                key={r.user_id}
                className="text-xs text-zinc-400 font-mono flex items-center justify-between"
              >
                <span>{r.user_name || r.user_email || r.user_id}</span>
                <span
                  className={
                    r.created > 0 ? "text-emerald-300" : "text-zinc-500"
                  }
                >
                  {r.created} task{r.created === 1 ? "" : "s"}
                  {r.error ? ` (${r.error})` : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
